import { useEffect, useState } from "react";

async function userOperation(operacion, datos = {}) {
  const response = await fetch("/api/dashboard/usuarios/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operacion, datos }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    throw new Error(payload.mensaje || `No fue posible completar la operación (HTTP ${response.status}).`);
  }
  return payload;
}

function roleLabel(role) {
  return String(role || "").replaceAll("_", " ");
}

export function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [initialRole, setInitialRole] = useState("REPARTIDOR");
  const [roleByUser, setRoleByUser] = useState({});
  const [credentialByUser, setCredentialByUser] = useState({});
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("");

  async function load() {
    setBusy("loading");
    try {
      const payload = await userOperation("listar");
      setUsers(payload.data?.usuarios || []);
      setRoles(payload.data?.roles_disponibles || []);
    } catch (error) {
      setStatus(error.message || "No fue posible consultar los usuarios.");
    } finally {
      setBusy("");
    }
  }

  useEffect(() => { load(); }, []);

  async function execute(key, operation, datos, message) {
    setBusy(key);
    setStatus("");
    try {
      const payload = await userOperation(operation, datos);
      setStatus(payload.mensaje || message);
      await load();
      return true;
    } catch (error) {
      setStatus(error.message || "No fue posible completar la operación.");
      return false;
    } finally {
      setBusy("");
    }
  }

  async function createUser(event) {
    event.preventDefault();
    const created = await execute("create", "crear", { nombre: name, codigo_rol: initialRole }, "Usuario creado.");
    if (created) setName("");
  }

  async function configureCredential(user) {
    const credential = credentialByUser[user.usuario_operativo_id] || {};
    if (!credential.username || String(credential.password || "").length < 6) {
      setStatus("Define un usuario y una contraseña temporal de mínimo 6 caracteres.");
      return;
    }
    const saved = await execute(
      `credential:${user.usuario_operativo_id}`,
      "configurar_credencial",
      { usuario_operativo_id: user.usuario_operativo_id, username: credential.username, password: credential.password },
      "Acceso configurado. La persona deberá cambiar su contraseña al ingresar."
    );
    if (saved) {
      setCredentialByUser((current) => ({
        ...current,
        [user.usuario_operativo_id]: { username: credential.username, password: "" },
      }));
    }
  }

  const isError = /no fue|no puedes|obligatorio|inválido|invalido|mínimo/i.test(status);

  return <section className="users-management">
    <header className="module-panel users-intro">
      <div>
        <p className="eyebrow">ACCESOS OPERATIVOS</p>
        <h2>Usuarios y roles</h2>
        <p>Los permisos pertenecen a los roles canónicos del ERP. Configura el acceso de cada persona sin compartir tu cuenta.</p>
      </div>
      <span className="record-count">{users.filter((user) => user.estado === "ACTIVO").length} activos</span>
    </header>

    <section className="module-panel user-create">
      <div><h3>Crear usuario</h3><p>Primero crea la persona y asígnale su función. Después podrás crear su acceso personal.</p></div>
      <form onSubmit={createUser}>
        <label>Nombre completo<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ej.: María González" required /></label>
        <label>Rol inicial<select value={initialRole} onChange={(event) => setInitialRole(event.target.value)}>{roles.map((role) => <option key={role.codigo} value={role.codigo}>{role.nombre}</option>)}</select></label>
        <button className="primary-action" disabled={Boolean(busy) || !roles.length}>{busy === "create" ? "Creando…" : "Crear usuario"}</button>
      </form>
    </section>

    {status && <p className={`action-status ${isError ? "error" : "success"}`} role="status">{status}</p>}

    <section className="module-panel users-list">
      <div className="module-heading"><div><p className="eyebrow">EQUIPO</p><h2>Usuarios operativos</h2></div><button className="secondary-action" onClick={load} disabled={Boolean(busy)}>{busy === "loading" ? "Actualizando…" : "Actualizar"}</button></div>
      {busy === "loading" && !users.length ? <div className="table-loading">Cargando usuarios…</div> : <div className="user-cards">
        {users.map((user) => {
          const active = user.estado === "ACTIVO";
          const selectedRole = roleByUser[user.usuario_operativo_id] || "";
          const credential = credentialByUser[user.usuario_operativo_id] || { username: user.credencial?.username || "", password: "" };
          const credentialState = user.credencial?.configurada ? (user.credencial?.requiere_cambio_password ? "Clave temporal pendiente de cambio" : "Acceso activo") : "Sin credenciales";
          return <article className={`user-card ${active ? "" : "inactive"}`} key={user.usuario_operativo_id}>
            <div className="user-card-heading"><div><b>{user.nombre || "Usuario sin nombre"}</b><small>{user.tipo_usuario || "INTERNO"}</small></div><span className={`status-pill ${active ? "activo" : "inactivo"}`}>{active ? "Activo" : "Inactivo"}</span></div>
            <div className="user-role-list">{(user.roles || []).map((role) => <span className="user-role" key={role}>{roleLabel(role)}<button type="button" aria-label={`Retirar ${roleLabel(role)}`} disabled={Boolean(busy) || (user.roles || []).length <= 1} onClick={() => window.confirm(`¿Retirar el rol ${roleLabel(role)} de ${user.nombre}?`) && execute(`remove:${user.usuario_operativo_id}:${role}`, "retirar_rol", { usuario_operativo_id: user.usuario_operativo_id, codigo_rol: role }, "Rol retirado.")}>×</button></span>)}</div>
            <div className="user-role-add"><select aria-label={`Agregar rol a ${user.nombre}`} value={selectedRole} onChange={(event) => setRoleByUser((current) => ({ ...current, [user.usuario_operativo_id]: event.target.value }))}><option value="">Agregar rol…</option>{roles.filter((role) => !(user.roles || []).includes(role.codigo)).map((role) => <option key={role.codigo} value={role.codigo}>{role.nombre}</option>)}</select><button type="button" className="secondary-action" disabled={!selectedRole || Boolean(busy)} onClick={() => execute(`role:${user.usuario_operativo_id}`, "asignar_rol", { usuario_operativo_id: user.usuario_operativo_id, codigo_rol: selectedRole }, "Rol asignado.")}>Asignar</button></div>
            <section className="user-credential">
              <div><b>Acceso web</b><small>{credentialState}</small></div>
              <label>Usuario<input value={credential.username} onChange={(event) => setCredentialByUser((current) => ({ ...current, [user.usuario_operativo_id]: { ...credential, username: event.target.value } }))} placeholder="ej. repartidor.1" autoComplete="off" /></label>
              <label>Contraseña temporal<input type="password" value={credential.password} onChange={(event) => setCredentialByUser((current) => ({ ...current, [user.usuario_operativo_id]: { ...credential, password: event.target.value } }))} placeholder="Mínimo 6 caracteres" autoComplete="new-password" /></label>
              <button type="button" className="credential-action" disabled={Boolean(busy)} onClick={() => configureCredential(user)}>{user.credencial?.configurada ? "Rotar acceso" : "Crear acceso"}</button>
            </section>
            <div className="user-card-actions"><button type="button" className={active ? "danger-action" : "primary-action"} disabled={Boolean(busy)} onClick={() => { const next = active ? "INACTIVO" : "ACTIVO"; if (window.confirm(`${active ? "¿Desactivar" : "¿Activar"} a ${user.nombre}?`)) execute(`state:${user.usuario_operativo_id}`, "cambiar_estado", { usuario_operativo_id: user.usuario_operativo_id, estado: next }, active ? "Usuario desactivado." : "Usuario activado."); }}>{active ? "Desactivar" : "Activar"}</button></div>
          </article>;
        })}
      </div>}
    </section>
  </section>;
}
